/** @jsxRuntime classic */
/** @jsx h */
import { autocomplete } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { debouncedSetInstantSearchUiState, getInstantSearchUiState, getInstantSearchUrl, setInstantSearchUiState, } from './instantsearch';
import { isModifierEvent } from './isModifierEvent';
import { searchClient } from './searchClient';
const gradeBandsMap = {
    '00': 'K-2',
    '01': '3-5',
    '02': '6-8',
    '03': '9-12',
    '04': 'AP® / College',
};
function onSelect({ setIsOpen, setQuery, event, query, item }) {
    // You want to trigger the default browser behavior if the event is modified.
    if (isModifierEvent(event)) {
        return;
    }
    setQuery(query);
    setIsOpen(false);
    window.location.href = item.url;
    /*setInstantSearchUiState({
        query
    });*/
}
function getItemUrl({ query }) {
    return getInstantSearchUrl({
        query,
    });
}
function generateGradeIntervals(values) {
    const intervals = [];
    if (values.length === 0) {
        return intervals;
    }
    let start = (values[0] === '00') ? 'K' : parseInt(values[0]);
    let end = (values[0] === '00') ? 'K' : parseInt(values[0]);
    for (let i = 1; i < values.length; i++) {
        const currentValue = parseInt(values[i]);
        if (currentValue - parseInt(values[i - 1]) === 1) {
            end = currentValue;
        }
        else {
            const interval = (start === 'K') ? 'K-' + end : start + '-' + end;
            intervals.push(interval);
            start = (values[i] === '00') ? 'K' : currentValue;
            end = (values[i] === '00') ? 'K' : currentValue;
        }
    }
    // Add the last interval
    const interval = (start === 'K') ? 'K-' + end : start + '-' + end;
    intervals.push(interval);
    return intervals;
}
function getItemWrapper({ item, html, children, query }) {
    const uiState = {
        query,
    };
    return html `<div
        class="grid grid-cols-12 items-center gap-x-4 ${item.class}"
    >
        <div class="col-span-9 sm:col-span-8">
            <a class="universal-search-link aa-ItemLink !flex items-center justify-items-start text-black base md:text-xl"
                href="${item.is_external_resource ? "/external-resources/" : ''}${item.is_internal_resource ? "/lesson-plans/" : ''}${item.is_internal_unit_plan ? "/lesson-plans/unit/" : ''}${item.slug}"
                onClick=${(event) => {
        if (!isModifierEvent(event)) {
            // Bypass the original link behavior if there's no event modifier
            // to set the InstantSearch UI state without reloading the page.
            // event.preventDefault();
        }
    }}
            >
                <div style="background-image: url('${item.image_conversions['thumb'] ?? item.image_conversions['360'] ?? item.image}')"
                     class="hidden md:block flex-0 bg-cover bg-center w-12 h-12 rounded-lg mr-4"
                ></div>
                <div class="flex-1 text-left truncate">
                    ${item.title ? item.title : children}
                </div>
            </a>
        </div>

        <div class="col-span-1 hidden sm:flex items-center justify-end">
            <span class="grades flex-0 gap-x-0 text-base justify-center rounded-md py-1 px-2  ${(item.hasOwnProperty('grade_range') && item.grade_range != null) ? '' : 'hidden'}">
                ${item.gradeBands[0]}
            </span>
        </div>

        <div class="col-span-3">
            
        </div>

    </div>`;
}
const snippet = function (text, length) {
    if (text.length > length) {
        return text.substring(0, length - 1) + '...';
    }
    return text;
};
const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'instantsearch',
    limit: 3,
    transformSource({ source }) {
        return {
            ...source,
            getItemUrl({ item }) {
                return getItemUrl({
                    query: item.label
                });
            },
            onSelect({ setIsOpen, setQuery, item, event }) {
                onSelect({
                    setQuery,
                    setIsOpen,
                    event,
                    query: item.label,
                    item: item
                });
            },
            templates: {
                ...source.templates,
                // Update the default `item` template to wrap it with a link
                // and plug it to the InstantSearch router.
                item(params) {
                    const { children } = source.templates.item(params).props;
                    const { item, html } = params;
                    return getItemWrapper({
                        item: item,
                        query: item.label,
                        html,
                        children,
                    });
                },
            },
        };
    },
});
const querySuggestionsPlugin = createQuerySuggestionsPlugin({
    searchClient: searchClient,
    indexName: 'teaching_materials',
    getSearchParams() {
        return recentSearchesPlugin.data.getAlgoliaSearchParams({
            hitsPerPage: 5,
            facetFilters: [
            // 'enabled:true'
            ],
        });
    },
    transformSource({ source }) {
        return {
            ...source,
            sourceId: 'querySuggestionsPlugin',
            getItemUrl({ item }) {
                return getItemUrl({
                    query: item.url
                });
            },
            onSelect({ setIsOpen, setQuery, event, item }) {
                onSelect({
                    setQuery,
                    setIsOpen,
                    event,
                    query: item.url,
                    item: item
                });
            },
            getItems(params) {
                if (!params.state.query) {
                    return [];
                }
                return source.getItems(params);
            },
            templates: {
                ...source.templates,
                header({ html }) {
                    return html `
                        <span class="uppercase text-black text-lg md:text-xl ml-2">Suggestions</span>
                  `;
                },
                footer(params) {
                    const { html } = params;
                    return html `
                        <div class="mt-2 pt-4 pb-2 border-t border-gray-200">
                            <a href="/search?term=${params.state.query}"
                               class="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            >
                                Filter search results
                            </a>
                        </div>
                  `;
                },
                item(params) {
                    const { children } = source.templates.item(params).props;
                    const { item, html } = params;
                    return getItemWrapper({
                        item: item,
                        query: item.query,
                        html,
                        children,
                    });
                },
            },
        };
    },
});
const searchPageState = getInstantSearchUiState();
export function startAutocomplete(searchInstance, containerId) {
    let skipInstantSearchStateUpdate = false;
    const { setQuery } = autocomplete({
        container: containerId ?? '#autocomplete',
        placeholder: 'Search for resources',
        openOnFocus: true,
        plugins: [
            //recentSearchesPlugin,
            querySuggestionsPlugin,
        ],
        detachedMediaQuery: 'none',
        initialState: {
            query: searchPageState.query || '',
        },
        navigator: {
            navigate() {
                // We don't navigate to a new page because we leverage the InstantSearch
                // UI state API.
            },
        },
        onSubmit({ state }) {
            setInstantSearchUiState({ query: state.query });
            window.location.href = `/search?term=${state.query}`;
        },
        onReset() {
            setInstantSearchUiState({
                query: '',
            });
        },
        onStateChange({ prevState, state }) {
            if (!skipInstantSearchStateUpdate && prevState.query !== state.query) {
                debouncedSetInstantSearchUiState({ query: state.query });
            }
            skipInstantSearchStateUpdate = false;
        },
    });
    window.addEventListener('popstate', () => {
        skipInstantSearchStateUpdate = true;
        setQuery((searchInstance.helper && searchInstance.helper.state.query) || '');
    });
}
